import { useEffect, useState } from "react";
import type { ReleaseConfiguration } from "@insights/contracts";
import {
  ActionIcon,
  Button,
  Card,
  ChipInput,
  Combobox,
  Input,
  Toast,
} from "@insights/ui";
import { Plus, Trash2 } from "lucide-react";
import { useParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { getReleaseConfigurationError } from "../helpers/release-configuration";
import { useBuildConfiguration } from "../hooks/useBuildConfiguration";
import "../styles/build-configuration.css";

const markerOptions = [
  { value: "emoji", label: "Emoji (default)" },
  { value: "shield", label: "Shields.io" },
] as const;
const colorOptions = [
  "lightgrey",
  "gray",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "indigo",
  "pink",
].map((color) => ({ value: color, label: color }));

export function ReleaseRulesPage() {
  const { workspaceId } = useParams();
  const { query, update, repositories } = useBuildConfiguration(workspaceId);
  const [draft, setDraft] = useState<ReleaseConfiguration | null>(null);
  const [failedConfiguration, setFailedConfiguration] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>();
  const canEdit = ["owner", "admin"].includes(query.data?.role ?? "");

  useEffect(() => {
    setDraft(null);
    setFailedConfiguration(null);
    setToastMessage(undefined);
  }, [workspaceId]);

  useEffect(() => {
    if (query.data?.configuration && !draft) {
      setDraft(structuredClone(query.data.configuration));
    }
  }, [draft, query.data?.configuration]);

  const validationError = draft
    ? getReleaseConfigurationError(draft)
    : null;
  const serializedDraft = draft ? JSON.stringify(draft) : "";
  const dirty = Boolean(
    draft &&
      serializedDraft !== JSON.stringify(query.data?.configuration),
  );

  useEffect(() => {
    if (!canEdit || !dirty || !validationError) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setToastMessage(validationError);
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [canEdit, dirty, validationError]);

  useEffect(() => {
    if (!toastMessage) {
      return;
    }

    const timeout = window.setTimeout(() => setToastMessage(undefined), 5000);
    return () => window.clearTimeout(timeout);
  }, [toastMessage]);

  useEffect(() => {
    if (
      !canEdit ||
      !draft ||
      !dirty ||
      validationError ||
      update.isPending ||
      serializedDraft === failedConfiguration
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      const snapshot = structuredClone(draft);
      const serializedSnapshot = JSON.stringify(snapshot);

      update.mutate(snapshot, {
        onError: (error) => {
          setFailedConfiguration(serializedSnapshot);
          setToastMessage(error.message);
        },
        onSuccess: (savedConfiguration) => {
          setFailedConfiguration(null);
          setDraft((current) =>
            current && JSON.stringify(current) === serializedSnapshot
              ? structuredClone(savedConfiguration)
              : current,
          );
        },
      });
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [
    canEdit,
    dirty,
    draft,
    failedConfiguration,
    serializedDraft,
    update,
    validationError,
  ]);

  if (query.isLoading || !draft) {
    return (
      <AppShell>
        <PageHeader eyebrow="Workspace settings" title="Release Rules" />
        <Loading />
      </AppShell>
    );
  }

  const disabled = !canEdit;

  return (
    <AppShell>
      <PageHeader eyebrow="Workspace settings" title="Release Rules" />
      {!canEdit && (
        <p className="build-config-notice">
          You can view these release rules, but only workspace owners and admins
          can change them.
        </p>
      )}
      {toastMessage && (
        <Toast
          onDismiss={() => setToastMessage(undefined)}
          title="Release rules not saved"
          tone="danger"
        >
          {toastMessage}
        </Toast>
      )}

      <div className="build-config-stack">
        <Card as="section" className="build-config-card">
          <div className="build-config-heading">
            <div>
              <h2>Release notes personality</h2>
              <p>Used when copying from Timeline or Comparison. Factual preserves the commit details; Pragmatic and Playful generate an engineering digest.</p>
            </div>
            <Combobox
              disabled={disabled}
              label="Release notes personality"
              options={[
                { value: "factual", label: "Factual" },
                { value: "pragmatic", label: "Pragmatic (default)" },
                { value: "playful", label: "Playful" },
              ] as const}
              value={draft.releaseNotesPersonality}
              onValueChange={(releaseNotesPersonality) => setDraft({ ...draft, releaseNotesPersonality })}
            />
          </div>
          <p>Copy provides rich text for compatible applications and Markdown for text editors.</p>
        </Card>
        <Card as="section" className="build-config-card">
          <div className="build-config-heading">
            <div>
              <h2>Release targets</h2>
              <p>Successful builds containing any of these targets become release points.</p>
            </div>
          </div>
          <ChipInput
            disabled={disabled}
            label="Add release target"
            onValueChange={(deploymentTargets) =>
              setDraft({ ...draft, deploymentTargets })
            }
            placeholder="Add target"
            values={draft.deploymentTargets}
          />
        </Card>

        <Card as="section" className="build-config-card">
          <div className="build-config-heading">
            <div>
              <h2>Release profiles</h2>
              <p>
                Select a repository first, then define its release environment and branch pattern.
              </p>
            </div>
            <Button
              color="neutral"
              disabled={disabled}
              icon={<Plus />}
              onClick={() =>
                setDraft({
                  ...draft,
                  profiles: [
                    ...draft.profiles,
                    { repository: "", name: "", environment: "", branchPattern: "" },
                  ],
                })
              }
              type="button"
            >
              Add profile
            </Button>
          </div>
          {repositories.isError && <p role="alert">Could not load repositories. Reload to try again.</p>}
          <div className="build-config-table-wrap">
            <table className="build-config-table build-config-mobile-stack build-config-profiles">
              <thead>
                <tr>
                  <th>Repository</th>
                  <th>Name</th>
                  <th>Environment</th>
                  <th>Branch pattern</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {draft.profiles.map((profile, index) => (
                  <tr key={index}>
                    <td data-label="Repository">
                      <Combobox
                        label={`Profile ${index + 1} repository`}
                        disabled={disabled || repositories.isLoading}
                        placeholder="Select a repository"
                        value={profile.repository ?? "__legacy_all__"}
                        options={[
                          ...(profile.repository === undefined
                            ? [{ value: "__legacy_all__", label: "All repositories (legacy)" }]
                            : []),
                          ...Array.from(new Set([
                            ...(repositories.data ?? []),
                            ...(profile.repository ? [profile.repository] : []),
                          ])).map((repository) => ({ value: repository, label: repository })),
                        ]}
                        onValueChange={(repository) => {
                          if (repository === "__legacy_all__") {
                            return;
                          }
                          setDraft({
                            ...draft,
                            profiles: draft.profiles.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, repository } : item,
                            ),
                          });
                        }}
                      />
                    </td>
                    {(["name", "environment", "branchPattern"] as const).map((field) => (
                      <td
                        data-label={
                          field === "branchPattern"
                            ? "Branch pattern"
                            : field === "environment"
                              ? "Environment"
                              : "Name"
                        }
                        key={field}
                      >
                        <Input
                          aria-label={`Profile ${index + 1} ${field}`}
                          disabled={disabled || profile.repository === ""}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              profiles: draft.profiles.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, [field]: event.target.value }
                                  : item,
                              ),
                            })
                          }
                          placeholder={field === "branchPattern" ? "main or release/*" : undefined}
                          value={profile[field]}
                        />
                      </td>
                    ))}
                    <td data-label="Actions">
                      <ActionIcon
                        className="build-config-delete"
                        color="danger"
                        disabled={disabled}
                        icon={<Trash2 />}
                        label={`Remove profile ${profile.name || index + 1}`}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            profiles: draft.profiles.filter((_, itemIndex) => itemIndex !== index),
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!draft.profiles.length && <p className="build-config-empty">No release profiles configured.</p>}
        </Card>

        <Card as="section" className="build-config-card">
          <div className="build-config-heading">
            <div>
              <h2>Release-note badges</h2>
              <p>Choose inline emojis or Shields.io badges for rendered and copied release notes.</p>
            </div>
            <Combobox
              disabled={disabled}
              label="Release-note marker style"
              onValueChange={(releaseNotesMarkerMode) =>
                setDraft({ ...draft, releaseNotesMarkerMode })
              }
              options={markerOptions}
              value={draft.releaseNotesMarkerMode}
            />
          </div>

          <h3>Fallback category</h3>
          <div className="build-config-fallback">
            {(["name", "emoji"] as const).map((field) => (
              <label key={field}>
                <span>{field}</span>
                <Input
                  content={field === "emoji" ? "emoji" : "text"}
                  disabled={disabled}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      releaseNotesFallbackCategory: {
                        ...draft.releaseNotesFallbackCategory,
                        [field]: event.target.value,
                      },
                    })
                  }
                  value={draft.releaseNotesFallbackCategory[field]}
                />
              </label>
            ))}
            {draft.releaseNotesMarkerMode === "shield" && (
              <label>
                <span>color</span>
                <Combobox
                  disabled={disabled}
                  label="Fallback badge color"
                  onValueChange={(color) =>
                    setDraft({
                      ...draft,
                      releaseNotesFallbackCategory: {
                        ...draft.releaseNotesFallbackCategory,
                        color,
                      },
                    })
                  }
                  options={colorOptions}
                  value={draft.releaseNotesFallbackCategory.color}
                />
              </label>
            )}
          </div>

          <div className="build-config-subheading">
            <h3>Categories</h3>
            <Button
              color="neutral"
              disabled={disabled}
              icon={<Plus />}
              onClick={() =>
                setDraft({
                  ...draft,
                  releaseNotesCategories: [
                    ...draft.releaseNotesCategories,
                    { name: "", emoji: "", color: "gray", aliases: [] },
                  ],
                })
              }
              type="button"
            >
              Add category
            </Button>
          </div>
          <div className="build-config-table-wrap">
            <table className="build-config-table build-config-mobile-stack build-config-categories">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>{draft.releaseNotesMarkerMode === "emoji" ? "Emoji" : "Color"}</th>
                  <th>Aliases</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {draft.releaseNotesCategories.map((category, index) => (
                  <tr key={index}>
                    <td data-label="Category">
                      <Input
                        aria-label={`Category ${index + 1} name`}
                        disabled={disabled}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            releaseNotesCategories: draft.releaseNotesCategories.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, name: event.target.value } : item,
                            ),
                          })
                        }
                        value={category.name}
                      />
                    </td>
                    <td
                      data-label={
                        draft.releaseNotesMarkerMode === "emoji"
                          ? "Emoji"
                          : "Color"
                      }
                    >
                      {draft.releaseNotesMarkerMode === "emoji" ? (
                        <Input
                          aria-label={`Category ${index + 1} emoji`}
                          content="emoji"
                          disabled={disabled}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              releaseNotesCategories: draft.releaseNotesCategories.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, emoji: event.target.value } : item,
                              ),
                            })
                          }
                          value={category.emoji}
                        />
                      ) : (
                        <Combobox
                          disabled={disabled}
                          label={`Category ${index + 1} color`}
                          onValueChange={(color) =>
                            setDraft({
                              ...draft,
                              releaseNotesCategories: draft.releaseNotesCategories.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, color } : item,
                              ),
                            })
                          }
                          options={colorOptions}
                          value={category.color}
                        />
                      )}
                    </td>
                    <td data-label="Aliases">
                      <ChipInput
                        disabled={disabled}
                        label={`Add alias to ${category.name || `category ${index + 1}`}`}
                        onValueChange={(aliases) =>
                          setDraft({
                            ...draft,
                            releaseNotesCategories: draft.releaseNotesCategories.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, aliases } : item,
                            ),
                          })
                        }
                        placeholder="Add alias"
                        values={category.aliases}
                      />
                    </td>
                    <td data-label="Actions">
                      <ActionIcon
                        className="build-config-delete"
                        color="danger"
                        disabled={disabled}
                        icon={<Trash2 />}
                        label={`Remove category ${category.name || index + 1}`}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            releaseNotesCategories: draft.releaseNotesCategories.filter((_, itemIndex) => itemIndex !== index),
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

    </AppShell>
  );
}
